// Paid lines on the fee slip screen are wanted until the statement showing them has been
// printed, and not after (Emma, 3 Nov 2026). Archiving takes a paid line out of the list and
// every total. Nothing about the line changes: its status, notes and payments stay where they
// are, a search still finds it, and it can be brought back.
//
// Only a line saved as Paid can be archived, and an archived line that stops being Paid is
// listed again on its own, so archiving can never hide something still owed.
//
// A line can also be deleted (Emma, 4 Nov 2026): out of the list and every total whatever
// its status. That is a mark in the same table, not a deletion from the line's own store, so
// a search finds it and it can be restored. An emailed line is dismissed instead, which hides
// it from every page that reads emailed items.
//
// Which lines are archived or deleted is kept in its own table (api/fee-slip-archive.js),
// keyed by the id the screen gives each line, because the lines come from four different
// stores.

import { normalizePaymentStatus } from "./clerkStatements.js";
import { amountOwing } from "./paymentReport.js";

export const FEE_SLIP_ARCHIVE_PATH = "/api/fee-slip-archive";

const isPaid = (row, statusOf) => normalizePaymentStatus(statusOf(row)) === "paid";
const round = (value) => Math.round(value * 100) / 100;

// archived: a Map (or Set) of line ids.
export function isArchived(row, archived, statusOf = (item) => item.status) {
  return Boolean(archived?.has(row.id)) && isPaid(row, statusOf);
}

export function withoutArchived(rows = [], archived = new Map(), statusOf = (item) => item.status) {
  if (!archived?.size) return rows;
  return rows.filter((row) => !isArchived(row, archived, statusOf));
}

export function withoutDeleted(rows = [], deleted = new Map()) {
  if (!deleted?.size) return rows;
  return rows.filter((row) => !deleted.has(row.id));
}

// Lines that can be archived now: saved as Paid, with no change waiting to be saved, and not
// archived already. A line just set to Paid on screen has not been saved, so it waits.
export function archivableRows(rows = [], { archived = new Map(), edits = {} } = {}) {
  return rows.filter((row) => normalizePaymentStatus(row.status) === "paid"
    && !edits[row.id]
    && !archived?.has(row.id));
}

// What is still owed on a list of lines: a paid line counts nothing, a part-paid one only
// what is left after the payments recorded against it.
export function unpaidTotal(rows = [], { statusOf = (item) => item.status, payments = [] } = {}) {
  return round(rows
    .filter((row) => !isPaid(row, statusOf))
    .reduce((sum, row) => sum + amountOwing(row, payments), 0));
}

export function paidLines(rows = [], statusOf = (item) => item.status) {
  const paid = rows.filter((row) => isPaid(row, statusOf));
  return {
    count: paid.length,
    total: round(paid.reduce((sum, row) => sum + (Number.parseFloat(row.invoiceAmount) || 0), 0)),
  };
}

// { archived, deleted }: each a map of line id -> { archived_at, archived_by }.
export async function fetchArchivedFeeSlips(brand, fetchImpl = globalThis.fetch) {
  const response = await fetchImpl(`${FEE_SLIP_ARCHIVE_PATH}?brand=${encodeURIComponent(brand)}`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!response.ok) throw new Error(`Archived fee slips returned ${response.status}`);
  const payload = await response.json();
  const archived = new Map();
  const deleted = new Map();
  for (const row of Array.isArray(payload) ? payload : []) {
    (row.reason === "deleted" ? deleted : archived).set(row.source_ref, row);
  }
  return { archived, deleted };
}

// The server takes this many at a time (api/fee-slip-archive.js).
export const ARCHIVE_BATCH = 500;

export function archiveFeeSlips(brand, rows = [], fetchImpl = globalThis.fetch) {
  return markFeeSlips(brand, rows, "paid", fetchImpl);
}

export function deleteFeeSlips(brand, rows = [], fetchImpl = globalThis.fetch) {
  return markFeeSlips(brand, rows, "deleted", fetchImpl);
}

async function markFeeSlips(brand, rows, reason, fetchImpl) {
  let archived = 0;
  for (let start = 0; start < rows.length; start += ARCHIVE_BATCH) {
    const response = await fetchImpl(FEE_SLIP_ARCHIVE_PATH, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        brand,
        reason,
        items: rows.slice(start, start + ARCHIVE_BATCH)
          .map((row) => ({ source_ref: row.id, clerk_name: row.clerkName || "" })),
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload?.error || `The ${reason === "deleted" ? "line could not be deleted" : "paid lines could not be archived"} (${response.status})`);
    }
    archived += Number(payload?.archived) || 0;
  }
  return { archived };
}

export function unarchiveFeeSlip(brand, id, fetchImpl = globalThis.fetch) {
  return restoreFeeSlip(brand, id, "paid", fetchImpl);
}

export function undeleteFeeSlip(brand, id, fetchImpl = globalThis.fetch) {
  return restoreFeeSlip(brand, id, "deleted", fetchImpl);
}

async function restoreFeeSlip(brand, id, reason, fetchImpl) {
  const response = await fetchImpl(FEE_SLIP_ARCHIVE_PATH, {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ brand, source_ref: id, reason, restore: true }),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `The line could not be brought back (${response.status})`);
  return payload;
}
